/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    const dialogs = [...this.querySelectorAll('dialog')];
    const images = dialogs.map(dialog => {
      const img = dialog.querySelector('[data-zoom-image]');
      return { id: dialog.id, src: img.src, srcset: img.srcset, alt: img.alt };
    });
    for (const [initial, dialog] of dialogs.entries()) {
      let index = initial;
      const update = (next) => {
        index = (next + images.length) % images.length;
        const source = images[index];
        const image = dialog.querySelector('[data-zoom-image]');
        image.srcset = source.srcset;
        image.src = source.src;
        image.alt = source.alt;
        image.loading = 'eager';
        for (const button of dialog.querySelectorAll('[data-zoom-target]')) {
          button.setAttribute('aria-pressed', String(button.dataset.zoomTarget === source.id));
        }
      };
      update(initial);
      dialog.addEventListener('command', event => {
        if (event.command === '--previous-image') { event.preventDefault(); update(index - 1); }
        if (event.command === '--next-image') { event.preventDefault(); update(index + 1); }
        if (event.command === '--select-image') {
          event.preventDefault();
          const next = images.findIndex(item => item.id === event.source?.dataset.zoomTarget);
          if (next >= 0) update(next);
        }
        if (event.command === 'show-modal') update(initial);
      }, opts);
      dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const rtl = getComputedStyle(this).direction === 'rtl';
          update(index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
        }
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
